import { UserOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Space, Tag, Typography } from 'antd';

import {
  DEV_IDENTITIES,
  INTENDED_ROUTE_KEY,
  safeIntendedRoute,
  setDevIdentity,
  type DevIdentity,
} from './devLogin';
import { defaultRouteForRole } from './RequireRole';
import { invalidateSession } from './useSession';

const { Title, Paragraph, Text } = Typography;

/**
 * Identity-picker shown in Dev Login mode (localhost + Dev only) in place of the
 * Entra redirect. Picking an identity stores its id and routes to that role's
 * dashboard. Four synthetic users (two originators + two approvers) so the lock
 * flow and other multi-user scenarios are testable without real Entra users.
 * Not reachable in QA/Prod — those environments never enable dev login.
 */
export function DevLoginPage() {
  const pick = (identity: DevIdentity) => {
    setDevIdentity(identity.id);
    // Reset both dashboards' remembered-tab state so a fresh identity
    // pick lands on the natural first tab, not on whatever the last
    // identity left open (localStorage is shared across identities on
    // this browser). Mirrors the same reset done in AppShell's
    // Switch-user affordance.
    window.localStorage.removeItem('bmo-inflow.originator.activeTab');
    window.localStorage.removeItem('bmo-inflow.approver.activeTab');
    const intended = safeIntendedRoute(sessionStorage.getItem(INTENDED_ROUTE_KEY));
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
    // Drop any /me cached under a previously-picked identity — reaching this
    // page does not guarantee the document was freshly loaded (dev logout
    // routes here client-side). Real SSO lands here via a full redirect, so a
    // document load matches that shape and leaves no module holding state
    // derived from the outgoing user.
    invalidateSession();
    window.location.replace(intended ?? defaultRouteForRole(identity.role));
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
      <Card style={{ maxWidth: 460, width: '100%' }}>
        <Title level={3} style={{ marginTop: 0 }}>
          Dev Login
        </Title>
        <Alert
          type="warning"
          showIcon
          message="Development sign-in — real SSO is pending"
          description="Pick a synthetic identity to continue. Distinct oids let us simulate multi-user scenarios (e.g., one Originator opening a workflow another Originator already has locked). This screen exists only on localhost and Dev."
          style={{ marginBottom: 20 }}
        />
        <Paragraph type="secondary">Continue as:</Paragraph>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {DEV_IDENTITIES.map((identity) => (
            <Button
              key={identity.id}
              block
              size="large"
              icon={<UserOutlined />}
              onClick={() => pick(identity)}
              style={{ textAlign: 'left', height: 56, display: 'flex', alignItems: 'center' }}
            >
              <span style={{ flex: 1, marginLeft: 8 }}>
                <Text strong>{identity.name}</Text>
              </span>
              <Tag
                color={
                  identity.role === 'Originator'
                    ? 'blue'
                    : identity.role === 'Approver'
                      ? 'green'
                      : 'purple'
                }
              >
                {identity.role}
              </Tag>
            </Button>
          ))}
        </Space>
      </Card>
    </div>
  );
}
